import {i18n} from '@lingui/core';
import {t, Trans} from '@lingui/macro';
import React from 'react';

import {Dictionary} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  AnalyticsGraphMetricsEnum,
  AnalyticsHeatMapMetricsEnum,
  AnalyticsMetricsKeys,
  AnalyticsMultiGraphMetricsEnum,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../models/api/analyticsMetricsModel';
import {helpCenterUrl} from '../../store/stateless/navigationActions';
import {AnalyticsHelpTooltip} from '../components/reportElements/tooltips/analyticsTooltips';
import {
  AnalyticsComputedGraphMetricsEnum,
  AnalyticsComputedProgressMetricsEnum,
} from '../transformers/analyticsMetricsTransformersTypes';

export interface MetricLabels {
  label: string;
  tooltip?: AnalyticsHelpTooltip;
}

export type AnyMetricKey =
  | AnalyticsMetricsKeys
  | AnalyticsComputedGraphMetricsEnum
  | AnalyticsComputedProgressMetricsEnum;

export interface MetricWithLabel<ID extends AnyMetricKey> extends MetricLabels {
  id: ID;

  // For tables
  columns?: Dictionary<MetricLabels>;
  // For multi-graphs
  series?: Array<MetricLabels>;
}

/**
 * Label and tooltip content for each metric.
 */
const metricsLabels: {
  [K in AnyMetricKey]?: MetricWithLabel<AnyMetricKey>;
} = {
  // Scalar
  // @TODO add missing metrics
  [AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS,
    label: i18n._(t('analyics-metric-active-segments')`Active conversations`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-active-segments-title">Active conversations</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-active-segments">
          Conversations with at least one activity (message, tag added, etc) this period
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/g9hlqkf/active-conversations`,
    },
  },
  [AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES]: {
    id: AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES,
    label: i18n._(t('analyics-metric-num-active-teammates')`Active teammates`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-active-teammates-title">Active teammates</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-active-teammates-time">
          The number of teammates with at least one activity (message, tag added, etc) this period
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6h1ms6`,
    },
  },
  [AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS,
    label: i18n._(t('analyics-metric-num-archived-segments')`Archived conversations`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-archived-segments-title">Archived conversations</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-archived-segments">
          Conversations archived or snoozed this period that remained archived at the end of the period
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
    },
  },
  [AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY]: {
    id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY,
    label: i18n._(t('analyics-metric-num-archived-segments-with-reply')`Archived with reply`),
    tooltip: {
      title: (
        <Trans id="analytics-metric-tooltip-num-archived-segments-with-reply-title">
          Archived with reply
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-archived-segments-with-reply">
          Conversations archived or snoozed this period that remained archived at the end of the period and
          had at least one prior reply
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/60hz9q7/archived-with-reply`,
    },
  },
  [AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME]: {
    id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
    label: i18n._(t('analyics-metric-avg-first-reply-time')`First reply time (avg)`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-avg-first-reply-time-title">First reply time (avg)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-avg-first-reply-time">
          Time from receiving the first message in a conversation to sending a first reply (average)
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/x2hlq1a/first-reply-time-avg`,
    },
  },
  [AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME]: {
    id: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
    label: i18n._(t('analyics-metric-avg-handle-time')`Handle time (avg)`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-avg-handle-time-title">Handle time (avg)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-avg-handle-time">
          Time from opening a conversation to sending a message (average)
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/35hlqpm/handle-time-avg`,
    },
  },
  [AnalyticsScalarMetricsEnum.AVG_TOTAL_REPLY_TIME]: {
    id: AnalyticsScalarMetricsEnum.AVG_TOTAL_REPLY_TIME,
    label: i18n._(t('analyics-metric-avg-total-reply-time')`Total reply time (avg)`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-avg-total-reply-time-title">Total reply time (avg)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-avg-total-reply-time">
          Cumulative time elapsed between receiving and replying to messages (average per conversation)
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc/total-reply-time-avg`,
    },
  },
  [AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED]: {
    id: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
    label: i18n._(t('analyics-metric-num-messages-received')`Messages received`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-messages-received-title">Messages received</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-messages-received">
          Inbound messages received from your customers
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/x2hlqpv/messages-received`,
    },
  },
  [AnalyticsScalarMetricsEnum.MESSAGES_SENT]: {
    id: AnalyticsScalarMetricsEnum.MESSAGES_SENT,
    label: i18n._(t('analyics-metric-num-messages-sent')`Messages sent`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-messages-sent-title">Messages sent</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-messages-sent">
          Outbound messages sent by your teammates
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/60hlqgg/messages-sent`,
    },
  },
  [AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT]: {
    id: AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
    label: i18n._(t('analyics-metric-num-new-segments')`New conversations`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-new-segments-title">New conversations</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-new-segments">
          New conversations started by customers or your teammates
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hlqjy/new-conversations`,
    },
  },
  [AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME]: {
    id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
    label: i18n._(t('analytics-metric-avg-response-time')`Reply time (avg)`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-avg-response-time-title">Reply time (avg)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-avg-response-time">
          Time from receiving a message to sending a reply (average)
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hlqr6/reply-time-avg`,
    },
  },

  [AnalyticsScalarMetricsEnum.PCT_SLA_ACHIEVED]: {
    id: AnalyticsScalarMetricsEnum.PCT_SLA_ACHIEVED,
    label: i18n._(t('analytics-metric-pct-sla-achievement')`SLA achievement (%)`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-pct-sla-achievement-title">SLA achievement (%)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-pct-sla-achieved">
          Percentage of messages with SLA conditions that did not end up in breach.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hk472`,
    },
  },
  [AnalyticsScalarMetricsEnum.NUM_SLA_TRIGGER]: {
    id: AnalyticsScalarMetricsEnum.NUM_SLA_TRIGGER,
    label: i18n._(t('analytics-metric-num-sla-trigger')`Messages with SLA`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-sla-trigger-title">Messages with SLA</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-sla-trigger">
          Number of messages that met SLA conditions this period
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/35hk47r`,
    },
  },
  [AnalyticsScalarMetricsEnum.SLA_BREACHES]: {
    id: AnalyticsScalarMetricsEnum.SLA_BREACHES,
    label: i18n._(t('analytics-metric-num-sla-breaches')`SLA breaches`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-sla-breaches-title">SLA breaches</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-sla-breaches">
          Number of times an SLA breach tag was applied this period.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
    },
  },
  [AnalyticsScalarMetricsEnum.SLA_WARNINGS]: {
    id: AnalyticsScalarMetricsEnum.SLA_WARNINGS,
    label: i18n._(t('analytics-metric-num-sla-warnings')`SLA warnings`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-num-sla-warnings-title">SLA warnings</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-num-sla-warnings">
          Number of times an SLA warning tag was applied this period.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hlqr2/sla-warnings`,
    },
  },
  [AnalyticsScalarMetricsEnum.AVG_SLA_BREACH_TIME]: {
    id: AnalyticsScalarMetricsEnum.AVG_SLA_BREACH_TIME,
    label: i18n._(t('analytics-metric-avg-sla-breach-time')`SLA breach time (avg)`),
    tooltip: {
      title: <Trans id="analytics-metric-tooltip-avg-sla-breach-time-title">SLA breach time (avg)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-tooltip-avg-sla-breach-time">
          Time from the SLA breach to the next reply (average).
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hlqrj/sla-breach-time-avg`,
    },
  },
  // Graph
  // @TODO add missing metrics
  [AnalyticsGraphMetricsEnum.ACTIVE_SEGMENTS_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.ACTIVE_SEGMENTS_GRAPH,
    label: i18n._(t('analytics-metric-active-segments-graph-label')`Active conversations`),
    tooltip: {
      title: <Trans id="analytics-metric-active-segments-graph-tooltip-title">Active conversations</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-active-segments-graph-tooltip">
          Conversations with at least one activity (message, tag added, etc) during this period
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/g9hlqkf/active-conversations`,
    },
  },
  [AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH,
    label: i18n._(t('analytics-metric-archived-segments-graph-label')`Archived conversations`),
    tooltip: {
      title: (
        <Trans id="analytics-metric-archived-segments-graph-tooltip-title">Archived conversations</Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-archived-segments-graph-tooltip">
          Conversations archived or snoozed this period that remained archived at the end of the period
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
    },
  },
  [AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH,
    label: i18n._(t('analytics-metric-archived-segments-with-reply-graph-label')`Archived with reply`),
    tooltip: {
      title: (
        <Trans id="analytics-metric-archived-segments-with-reply-graph-tooltip-title">
          Archived with reply
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-archived-segments-with-reply-graph-tooltip">
          Conversations archived or snoozed this period that remained archived at the end of the period and
          had at least one prior reply
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/60hz9q7/archived-with-reply`,
    },
  },
  [AnalyticsGraphMetricsEnum.CSAT_SURVEY_RESPONSE_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.CSAT_SURVEY_RESPONSE_GRAPH,
    label: i18n._(t('analytics-metric-csat-survey-response-graph-label')`Satisfaction score (avg)`),
    tooltip: {
      title: (
        <Trans id="analytics-metric-csat-survey-response-graph-tooltip-title">Satisfaction score (avg)</Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-csat-survey-response-graph-tooltip">
          Average score of survey responses this period
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hk3qx`,
    },
  },
  [AnalyticsGraphMetricsEnum.CSAT_SURVEY_SATISFACTION_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.CSAT_SURVEY_SATISFACTION_GRAPH,
    label: i18n._(t('analytics-metric-csat-survey-satisfaction-graph-label')`Satisfaction score (%)`),
    tooltip: {
      title: (
        <Trans id="analytics-metric-csat-survey-satisfaction-graph-tooltip-title">
          Satisfaction score (%)
        </Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-csat-survey-satisfaction-graph-tooltip">
          Percent of surveys responded to this period with 4 or 5 stars.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hfdgd/satisfaction-score`,
    },
  },
  [AnalyticsScalarMetricsEnum.PCT_CSAT_SURVEY_SATISFACTION]: {
    id: AnalyticsScalarMetricsEnum.PCT_CSAT_SURVEY_SATISFACTION,
    label: i18n._(t('analytics-metric-pct-csat-survey-satisfaction-label')`Overall Satisfaction`),
    tooltip: {
      title: (
        <Trans id="analytics-metric-pct-csat-survey-satisfaction-tooltip-title">Overall Satisfaction</Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-pct-csat-survey-satisfaction-tooltip">
          Percent of surveys responded to this period with 4 or 5 stars.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hfdgd/satisfaction-score`,
    },
  },
  [AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH,
    label: i18n._(t('analytics-metric-first-response-graph-label')`First reply time (avg)`),
    tooltip: {
      title: <Trans id="analytics-metric-first-response-graph-tooltip-title">First reply time (avg)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-first-response-graph-tooltip">
          Time from receiving the first message in a conversation to sending a first reply (average)
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/x2hlq1a/first-reply-time-avg`,
    },
  },
  [AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH,
    label: i18n._(t('analytics-metric-handle-time-graph-label')`Handle time (avg)`),
    tooltip: {
      title: <Trans id="analytics-metric-handle-time-graph-tooltip-title">Handle time (avg)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-handle-time-graph-tooltip">
          Time from opening a conversation to sending a message (average)
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/35hlqpm/handle-time-avg`,
    },
  },
  [AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH,
    label: i18n._(t('analytics-metric-messages-received-graph-label')`Messages received`),
    tooltip: {
      title: <Trans id="analytics-metric-messages-received-graph-tooltip-title">Messages received</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-messages-received-graph-tooltip">
          Inbound messages received from your customers
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/x2hlqpv/messages-received`,
    },
  },
  [AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
    label: i18n._(t('analytics-metric-new-conversations-graph-label')`New conversations`),
    tooltip: {
      title: <Trans id="analytics-metric-new-conversations-graph-tooltip-title">New conversations</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-new-conversations-graph-tooltip">
          New conversations started by customers or your teammates
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
    },
  },
  [AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH,
    label: i18n._(t('analytics-metric-num-messages-sent-graph-label')`Messages sent`),
    tooltip: {
      title: <Trans id="analytics-metric-num-messages-sent-graph-tooltip-title">Messages sent</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-num-messages-sent-graph-tooltip">
          Outbound messages sent by your teammates
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/60hlqgg/messages-sent`,
    },
  },
  [AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH,
    label: i18n._(t('analytics-metric-num-sla-breach-graph-label')`SLA breaches`),
    tooltip: {
      title: <Trans id="analytics-metric-num-sla-breach-graph-tooltip-title">SLA breaches</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-num-sla-breach-graph-tooltip">
          Number of times an SLA breach tag was applied
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
    },
  },
  [AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH,
    label: i18n._(t('analytics-metric-num-sla-warning-graph-label')`SLA warnings`),
    tooltip: {
      title: <Trans id="analytics-metric-num-sla-warning-graph-tooltip-title">SLA warnings</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-num-sla-warning-graph-tooltip">
          Number of times an SLA warning tag was applied
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hlqr2/sla-warnings`,
    },
  },
  [AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH,
    label: i18n._(t('analytics-metric-pct-sla-achievement-graph-label')`SLA achievement (%)`),
    tooltip: {
      title: <Trans id="analytics-metric-pct-sla-achievement-graph-tooltip-title">SLA achievement (%)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-pct-sla-achievement-graph-tooltip">
          Percentage of messages with SLA conditions that did not end up in breach.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hk472`,
    },
  },
  [AnalyticsGraphMetricsEnum.RESPONSE_GRAPH]: {
    id: AnalyticsGraphMetricsEnum.RESPONSE_GRAPH,
    label: i18n._(t('analytics-metric-response-graph-label')`Reply time (avg)`),
    tooltip: {
      title: <Trans id="analytics-metric-response-graph-tooltip-title">Reply time (avg)</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-response-graph-tooltip">
          Time from receiving a message to sending a reply (average)
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hlqr6/reply-time-avg`,
    },
  },
  // Heat map
  [AnalyticsHeatMapMetricsEnum.INBOX]: {
    id: AnalyticsHeatMapMetricsEnum.INBOX,
    label: i18n._(t('analytics-metric-inbox-heat-map-title')`Busiest Times`),
    tooltip: {
      title: <Trans id="analytics-metric-inbox-heat-map-tooltip-title">Busiest Times</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-inbox-heat-map-tooltip">
          Busiest times for your team to help your staffing decisions. The darker the rectangle, the busier
          your team is.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}t/y4hz4m0/team-performance-report#busiest-times-heatmap`,
    },
  },
  // Used in the heat map
  [AnalyticsScalarMetricsEnum.NUM_NEW_SEGMENTS]: {
    id: AnalyticsScalarMetricsEnum.NUM_NEW_SEGMENTS,
    label: i18n._(t('analytics-metric-inbox-heat-map-num-new-segments-label')`New conversations`),
    tooltip: {
      title: (
        <Trans id="analytics-metric-inbox-heat-map-num-new-segments-tooltip-title">New conversations</Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-inbox-heat-map-num-new-segments-tooltip">
          New conversations started by customers or your teammates
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hlqjy/new-conversations`,
    },
  },
  // Multi graph
  [AnalyticsMultiGraphMetricsEnum.SLA_PERFORMANCE_GRAPH]: {
    id: AnalyticsMultiGraphMetricsEnum.SLA_PERFORMANCE_GRAPH,
    label: i18n._(t('analytics-metric-sla-performance-graph-label')`SLA Performance Over Time`),
    tooltip: {
      title: (
        <Trans id="analytics-metric-sla-performance-graph-tooltip-title">SLA performance over time</Trans>
      ),
      tooltipContent: (
        <Trans id="analytics-metric-sla-performance-graph-tooltip">
          Number of breaches and warnings for a specific SLA Rule over time.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hh1bw/sla-rules`,
    },
    series: [
      {
        label: i18n._(t('analytics-metric-sla-performance-graph-warnings-label')`Warnings`),
        tooltip: {
          title: <Trans id="analytics-metric-sla-performance-graph-warnings-tooltip-title">Warnings</Trans>,
          tooltipContent: (
            <Trans id="analytics-metric-sla-performance-graph-warnings-tooltip">
              Number of times an SLA warning tag was applied this period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqr2/sla-warnings`,
        },
      },
      {
        label: i18n._(t('analytics-metric-sla-performance-graph-breaches-label')`Breaches`),
        tooltip: {
          title: <Trans id="analytics-metric-sla-performance-graph-breaches-tooltip-title">Breaches</Trans>,
          tooltipContent: (
            <Trans id="analytics-metric-sla-performance-graph-breaches-tooltip">
              Number of times an SLA breach tag was applied this period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
        },
      },
    ],
  },
  [AnalyticsComputedGraphMetricsEnum.REPLY_TIME_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.REPLY_TIME_GRAPH,
    label: i18n._(t('analytics-overview-report-response-time-graph-title')`Reply times`),
    tooltip: {
      title: <Trans id="analytics-overview-report-response-times-tooltip-title">Reply times</Trans>,
      tooltipContent: (
        <Trans id="analytics-overview-report-response-times-tooltip">
          The proportion of conversations replied to within different time ranges
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hlqr6`,
    },
  },
  [AnalyticsComputedGraphMetricsEnum.RESOLUTION_TIME_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.RESOLUTION_TIME_GRAPH,
    label: i18n._(t('analytics-overview-report-resolution-time-graph-title')`Total reply times`),
    tooltip: {
      title: <Trans id="analytics-overview-report-resolution-times-tooltip-title">Total reply times</Trans>,
      tooltipContent: (
        <Trans id="analytics-overview-report-resolution-times-tooltip">
          Total time your customer spent waiting for replies across the entire conversation.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc`,
    },
  },
  [AnalyticsComputedGraphMetricsEnum.FIRST_REPLY_TIME_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.FIRST_REPLY_TIME_GRAPH,
    label: i18n._(t('analytics-overview-report-first-reply-time-graph-title')`First reply times`),
    tooltip: {
      title: <Trans id="analytics-overview-report-first-reply-times-tooltip-title">First reply times</Trans>,
      tooltipContent: (
        <Trans id="analytics-overview-report-first-reply-times-tooltip">
          Time your customer spent waiting for the first reply.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/x2hlq1a`,
    },
  },
  [AnalyticsComputedGraphMetricsEnum.WHAT_HAPPENED_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.WHAT_HAPPENED_GRAPH,
    label: i18n._(t('analytics-metric-what-happened-table-title')`What Happened`),
    tooltip: {
      title: <Trans id="analytics-metric-what-happened-table-tooltip-title">What happened chart</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-what-happened-table-tooltip">
          Conversations that are new, closed, and remain open in your backlog each 24 hr day.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hz4mb/conversations-report#what-happened-chart`,
    },
    series: [
      {
        label: i18n._(t('analytics-new-workload')`New workload`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-what-happened-table-new-segments-tooltip-title">New workload</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-what-happened-table-new-segments-tooltip">
              Conversations that were new, reopened, or moved into inboxes
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlq22/new-workload`,
        },
      },
      {
        label: i18n._(t('analytics-closed-workload')`Closed workload`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-what-happened-table-closed-segments-tooltip-title">
              Closed workload
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-what-happened-table-closed-segments-tooltip">
              Conversations archived or moved out of inboxes
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlqbh/closed-workload`,
        },
      },
      {
        label: i18n._(t('analytics-open-at-end')`Open at end`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-what-happened-table-segments-remaining-open-tooltip-title">
              Open at end
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-what-happened-table-segments-remaining-open-tooltip">
              Conversations still open at the end of the period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlqjs`,
        },
      },
    ],
  },
  // Table
  [AnalyticsTableMetricsEnum.TEAM_CHANGES]: {
    id: AnalyticsTableMetricsEnum.TEAM_CHANGES,
    label: i18n._(t('analytics-metric-team-changes-table-title')`Team`),
    tooltip: {
      title: <Trans id="analytics-metric-team-changes-table-tooltip-title">Team</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-team-changes-table-tooltip">
          Teammates with the greatest increases and decreases compared to the previous time period of the same
          length
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hz4mn/overview-report#team-list`,
    },
  },
  [AnalyticsTableMetricsEnum.TOP_TAGS]: {
    id: AnalyticsTableMetricsEnum.TOP_TAGS,
    label: i18n._(t('analytics-metric-top-tags-table-title')`Top Tags`),
    tooltip: {
      title: <Trans id="analytics-metric-top-tags-table-tooltip-title">Top tags</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-top-tags-table-tooltip">
          Tags most often associated with conversations during the period, with changes from the previous
          period of the same length
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/m1hz4mn/overview-report#top-tags-list`,
    },
  },
  [AnalyticsTableMetricsEnum.INBOX_TABLE]: {
    id: AnalyticsTableMetricsEnum.INBOX_TABLE,
    label: i18n._(t('analytics-metric-inboxes-table-title')`Inboxes`),
    tooltip: {
      title: <Trans id="analytics-metric-inboxes-table-tooltip-title">Inboxes</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-inboxes-table-tooltip">
          Key metrics displayed by inbox. You can customize metrics using the gear icon.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hz4mb/conversations-report#inboxes-table`,
    },
    columns: {
      inbox: {
        label: i18n._(t('analytics-metric-inbox-table-columns-inbox-label')`Inbox`),
        tooltip: {
          title: <Trans id="analytics-metric-inbox-table-columns-inbox-tooltip-title">Inbox</Trans>,
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-inbox-tooltip">
              Conversations already open at the start of the period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/p8hz4mb/conversations-report#inboxes-table`,
        },
      },
      open_at_start: {
        label: i18n._(t('analytics-open-at-start')`Open at start`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-open-at-start-tooltip-title">Open at start</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-open-at-start-tooltip">
              Conversations that were already open at the start of the time period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/83hlq5t`,
        },
      },
      active: {
        label: i18n._(t('analytics-metric-inbox-table-columns-active-label')`Active conversations`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-active-tooltip-title">Active conversations</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-active-tooltip">
              Conversations with at least one activity (message, tag added, etc) during this time period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/g9hlqkf`,
        },
      },
      new_direct_in_inbox: {
        label: i18n._(t('analytics-metric-inbox-table-columns-new-direct-in-inbox-label')`New conversations`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-new-direct-in-inbox-tooltip-title">
              New conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-new-direct-in-inbox-tooltip">
              New conversations started by customers or your teammates
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqjy`,
        },
      },
      moved_into_inbox: {
        label: i18n._(t('analytics-metric-inbox-table-columns-moved-into-inbox-label')`Moved into inbox`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-moved-into-inbox-tooltip-title">
              Moved into inbox
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-moved-into-inbox-tooltip">
              Conversations moved into the inbox from another inbox
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlqg0`,
        },
      },
      reopened: {
        label: i18n._(t('analytics-metric-inbox-table-columns-reopened-label')`Reopened`),
        tooltip: {
          title: <Trans id="analytics-metric-inbox-table-columns-reopened-tooltip-title">Reopened</Trans>,
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-reopened-tooltip">
              Conversations reopened this period that were archived at the start of the period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/83hlqr3`,
        },
      },
      archived_in_inbox: {
        label: i18n._(t('analytics-metric-inbox-table-columns-archived-in-inbox-label')`Archived in inbox`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-tooltip-title">
              Archived in inbox
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf`,
        },
      },
      archived_in_inbox_without_reply: {
        label: i18n._(
          t(
            'analytics-metric-inbox-table-columns-archived-in-inbox-without-reply-label',
          )`Archived without reply`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-without-reply-tooltip-title">
              Archived without reply
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-without-reply-tooltip">
              Conversations that were archived with no reply in the past sent by your teammates
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qb/archived-without-reply`,
        },
      },
      archived_in_inbox_with_reply: {
        label: i18n._(
          t('analytics-metric-inbox-table-columns-archived-in-inbox-with-reply-label')`Archived with reply`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-with-reply-tooltip-title">
              Archived with reply
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-archived-in-inbox-with-reply-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
              and had at least one reply
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hz9q7`,
        },
      },
      moved_out_of_inbox: {
        label: i18n._(t('analytics-metric-inbox-table-columns-moved-out-of-inbox-label')`Moved out of inbox`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-moved-out-of-inbox-tooltip-title">
              Moved out of inbox
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-moved-out-of-inbox-tooltip">
              Conversations moved out of the inbox into another inbox
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqjh`,
        },
      },
      open_at_end: {
        label: i18n._(t('analytics-open-at-end')`Open at end`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-open-at-end-tooltip-title">Open at end</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-open-at-end-tooltip">
              Conversations still open at the end of the period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlqjs`,
        },
      },
      messages_sent: {
        label: i18n._(t('analytics-metric-inbox-table-columns-messages-sent-label')`Messages sent`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-messages-sent-tooltip-title">Messages sent</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-messages-sent-tooltip">
              Outbound messages sent by your teammates
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqgg`,
        },
      },
      messages_received: {
        label: i18n._(t('analytics-metric-inbox-table-columns-messages-received-label')`Messages received`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-inbox-table-columns-messages-received-tooltip-title">
              Messages received
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-inbox-table-columns-messages-received-tooltip">
              Inbound messages received from your customers
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlqpv`,
        },
      },
    },
  },
  [AnalyticsTableMetricsEnum.MESSAGE_TEMPLATE_TABLE]: {
    id: AnalyticsTableMetricsEnum.MESSAGE_TEMPLATE_TABLE,
    label: i18n._(t('analytics-metrics-message-template-table-title')`Message Template Details`),
    tooltip: {
      title: <Trans id="tags-report-message-template-table-tooltip-title">Message template details</Trans>,
      tooltipContent: (
        <Trans id="tags-report-message-template-table-tooltip">Key metrics per message template used.</Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/y4hg2md/message-templates-report`,
    },
    columns: {
      num_messages_sent: {
        label: i18n._(
          t('analytics-metric-message-template-table-num-messages-sent-column-label')`Messages sent`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-message-template-table-num-messages-sent-column-tooltip-title">
              Messages sent
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-message-template-table-num-messages-sent-column-tooltip">
              Outbound messages sent by your teammates
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqgg`,
        },
      },
      num_replies_sent: {
        label: i18n._(
          t('analytics-metric-message-template-table-num-replies-sent-column-label')`Replies sent`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-message-template-table-num-replies-sent-column-tooltip-title">
              Replies sent
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-message-template-table-num-replies-sent-column-tooltip">
              Outbound messages sent by your teammates in reply to an inbound message
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/35hlqr8/replies-sent`,
        },
      },
      avg_handle_time: {
        label: i18n._(
          t('analytics-metric-message-template-table-avg-handle-time-column-label')`Handle time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="aanalytics-metric-message-template-table-avg-handle-time-column-tooltip-title">
              Handle Time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-message-template-table-avg-handle-time-column-tooltip">
              Time from opening a conversation to sending a message (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/35hlqpm/handle-time-avg`,
        },
      },
      customer_reply_rate: {
        label: i18n._(
          t('analytics-metric-message-template-table-customer-reply-rate-column-label')`Customer reply rate`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-message-template-table-customer-reply-rate-column-tooltip-title">
              Customer reply rate
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-message-template-table-customer-reply-rate-column-tooltip">
              Percentages of messages sent that received an inbound reply from the customer
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hgmhb/customer-reply-rate`,
        },
      },
    },
  },
  [AnalyticsTableMetricsEnum.TAGS_ALL_TABLE]: {
    id: AnalyticsTableMetricsEnum.TAGS_ALL_TABLE,
    label: i18n._(t('analytics-metrics-tags-all-table-title')`Tag Details`),
    tooltip: {
      title: <Trans id="tags-report-tags-all-table-tooltip-title">Tag details</Trans>,
      tooltipContent: (
        <Trans id="tags-report-tags-all-table-tooltip">
          Key metrics by tag during the selected time period. You can customize the metrics by clicking on the
          gear icon. Learn more.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hz4mc#tag-details-table`,
    },
    columns: {
      num_active_segments: {
        label: i18n._(
          t('analytics-metric-tags-all-table-num-active-segments-column-label')`Conversations tagged`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-num-active-segments-column-tooltip-title">
              Conversations tagged
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-num-active-segments-column-tooltip">
              Conversations with this tag and at least one activity (message, tag added, etc) this period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqk9/conversations-tagged`,
        },
      },
      pct_active_segments: {
        label: i18n._(
          t('analytics-metric-tags-all-table-pct-active-segments-column-label')`Conversations tagged (%)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-pct-active-segments-column-tooltip-title">
              Conversations tagged (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-pct-active-segments-column-tooltip">
              Percent of active conversations this period with this tag
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/p8hlq18/conversations-tagged`,
        },
      },
      avg_reply_time: {
        label: i18n._(t('analytics-metric-tags-all-table-avg-reply-time-column-label')`Reply time (avg)`),
        tooltip: {
          title: (
            <Trans id="aanalytics-metric-tags-all-table-avg-reply-time-column-tooltip-title">
              Reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-avg-reply-time-column-tooltip">
              Time from receiving a message to sending a reply (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlqr6/reply-time-avg`,
        },
      },
      avg_first_reply_time: {
        label: i18n._(
          t('analytics-metric-tags-all-table-avg-first-reply-time-column-label')`First reply time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-avg-first-reply-time-column-tooltip-title">
              First reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-avg-first-reply-time-column-tooltip">
              Time from receiving the first message in a conversation to sending a first reply (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlq1a/first-reply-time-avg`,
        },
      },
      avg_reaction_time: {
        label: i18n._(
          t('analytics-metric-tags-all-table-avg-reaction-time-column-label')`Reaction time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-avg-reaction-time-column-tooltip-title">
              Reaction time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-avg-reaction-time-column-tooltip">
              Time from receiving a message to an assign, reply or archive action (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlq57/reaction-time-avg`,
        },
      },
      avg_handle_time: {
        label: i18n._(t('analytics-metric-tags-all-table-avg-handle-time-column-label')`Handle time (avg)`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-avg-handle-time-column-tooltip-title">
              Handle time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-avg-handle-time-column-tooltip">
              Time from opening a conversation to sending a message (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/35hlqpm/handle-time-avg`,
        },
      },
      num_messages_received: {
        label: i18n._(
          t('analytics-metric-tags-all-table-num-messages-received-column-label')`Messages received`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-num-messages-received-column-tooltip-title">
              Messages received
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-num-messages-received-column-tooltip">
              Inbound messages received from your customers.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlqpv/messages-received`,
        },
      },
      num_messages_sent: {
        label: i18n._(t('analytics-metric-tags-all-table-num-messages-sent-column-label')`Messages sent`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-num-messages-sent-column-tooltip-title">
              Messages sent
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-num-messages-sent-column-tooltip">
              Outbound messages sent by your teammates
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqgg/messages-sent`,
        },
      },
      num_new_segments: {
        label: i18n._(t('analytics-metric-tags-all-table-num-new-segments-column-label')`New conversations`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-num-new-segments-column-tooltip-title">
              New conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-num-new-segments-column-tooltip">
              New conversations started by customers or your teammates
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqjy/new-conversations`,
        },
      },
      num_archived_segments: {
        label: i18n._(
          t('analytics-metric-tags-all-table-num-archived-segments-column-label')`Archived conversations`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-num-archived-segments-column-tooltip-title">
              Archived conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-num-archived-segments-column-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
        },
      },
      num_archived_segments_with_reply: {
        label: i18n._(
          t(
            'analytics-metric-tags-all-table-num-archived-segments-with-reply-column-label',
          )`Archived with reply`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-all-table-num-archived-segments-with-reply-column-tooltip-title">
              Archived with reply
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-all-table-num-archived-segments-with-reply-column-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
              and had at least one prior reply
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hz9q7/archived-with-reply`,
        },
      },
    },
  },
  [AnalyticsComputedProgressMetricsEnum.TAG_REPORT_PROGRESS]: {
    id: AnalyticsComputedProgressMetricsEnum.TAG_REPORT_PROGRESS,
    label: i18n._(t('analytics-metrics-tag-pct-conversations-title')`Conversations With Tags`),
    tooltip: {
      title: <Trans id="tag-pct-conversations-tooltip-title">Conversations with tags</Trans>,
      tooltipContent: (
        <Trans id="tag-pct-conversations-tooltip">
          This table shows how tags were added and removed over the report period. If you use tags to capture
          internal processes, you will be able to see how those processes are performing.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h4244c/understanding-tags`,
    },
  },
  [AnalyticsComputedProgressMetricsEnum.MESSAGE_TEMPLATE_REPORT_PROGRESS]: {
    id: AnalyticsComputedProgressMetricsEnum.MESSAGE_TEMPLATE_REPORT_PROGRESS,
    label: i18n._(t('analytics-metrics-messages-pct-message-templates-title')`Messages With Templates`),
    tooltip: {
      title: <Trans id="pct-message-templates-tooltip-title">Messages with templates</Trans>,
      tooltipContent: (
        <Trans id="pct-message-templates-tooltip">
          Percentage of outbound messages sent using a message template. Using message templates can help you
          reply faster to your customers.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/83hg2mb/messages-with-templates`,
    },
  },
  [AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE]: {
    id: AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE,
    label: i18n._(t('analytics-metrics-tags-changes-table-title')`Tag Changes`),
    tooltip: {
      title: <Trans id="tags-report-tags-changes-table-tooltip-title">Tag changes</Trans>,
      tooltipContent: (
        <Trans id="tags-report-tags-changes-table-tooltip">
          This table shows how tags were added and removed over the report period. If you use tags to capture
          internal processes, you will be able to see how those processes are performing.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/h7hz4mc#tag-changes-table`,
    },
    columns: {
      num_active_segments: {
        label: i18n._(
          t(
            'analytics-metric-tags-changes-table-num-active-segments-column-label',
          )`Conversations with tag changes`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-changes-table-num-active-segments-column-tooltip-title">
              Conversations with tag changes
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-changes-table-num-active-segments-column-tooltip">
              Conversations with at least one tag addition or removal this period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlscr/conversations-with-tag-changes`,
        },
      },
      num_added: {
        label: i18n._(t('analytics-metric-tags-changes-table-num-added-column-label')`Added this period`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-changes-table-num-added-column-tooltip-title">
              Added this period
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-changes-table-num-added-column-tooltip">
              Number of times this tag was added this period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/y4hlscw/added-this-period`,
        },
      },
      num_removed: {
        label: i18n._(t('analytics-metric-tags-changes-table-num-removed-column-label')`Removed this period`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-changes-table-num-removed-column-tooltip-title">
              Removed this period
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-changes-table-num-removed-column-tooltip">
              Number of times this tag was removed this period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hls4g/removed-this-period`,
        },
      },
      avg_tag_duration: {
        label: i18n._(
          t('analytics-metric-tags-changes-table-avg-tag-duration-column-label')`Tag duration (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-tags-changes-table-avg-tag-duration-column-tooltip-title">
              Tag duration (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-tags-changes-table-avg-tag-duration-column-tooltip">
              Time from applying a tag to removing it (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/83hls4j/tag-duration-avg`,
        },
      },
    },
  },
  [AnalyticsTableMetricsEnum.TEAM_PERFORMANCE]: {
    id: AnalyticsTableMetricsEnum.TEAM_PERFORMANCE,
    label: i18n._(t('analytics-metric-team-performance-table-label')`Team Performance`),
    tooltip: {
      title: <Trans id="analytics-metric-team-performance-table-tooltip-title">Team performance</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-team-performance-table-tooltip">
          The performance of individual teammates. You can customize the metrics by clicking on the gear icon.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/y4hz4m0/team-performance-report#teammates-table`,
    },
    columns: {
      teammate: {
        label: i18n._(t('analytics-metric-team-performance-table-teammate-column-label')`Teammate`),
      },
      avg_reply_time: {
        label: i18n._(
          t('analytics-metric-team-performance-table-avg-reply-time-column-label')`Reply time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-reply-time-column-tooltip-title">
              Reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-reply-time-column-tooltip">
              Time from receiving a message to sending a reply (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlqr6`,
        },
      },
      avg_first_reply_time: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-avg-first-reply-time-column-label',
          )`First reply time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-first-reply-time-column-tooltip-title">
              First reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-first-time-column-tooltip">
              Time from receiving the first message in a conversation to sending a first reply (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlq1a/first-reply-time-avg`,
        },
      },
      avg_total_reply_time: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-avg-total-reply-time-column-label',
          )`Total reply time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-total-reply-time-column-tooltip-title">
              Total reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-total-time-column-tooltip">
              Cumulative time elapsed between receiving and replying to messages (average per conversation)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc`,
        },
      },
      avg_handle_time: {
        label: i18n._(
          t('analytics-metric-team-performance-table-avg-handle-time-column-label')`Handle time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-handle-time-column-tooltip-title">
              Handle time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-handle-time-column-tooltip">
              Time from opening a conversation to sending a message (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/35hlqpm/handle-time-avg`,
        },
      },
      avg_resolution_time: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-avg-resolution-time-column-label',
          )`Resolution Time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-resolution-time-column-tooltip-title">
              Resolution time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-resolution-time-column-tooltip">
              Time between first inbound and final archive of a conversation (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc/total-reply-time-avg`,
        },
      },
      avg_reaction_time: {
        label: i18n._(
          t('analytics-metric-team-performance-table-avg-reaction-time-column-label')`Reaction time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-reaction-time-column-tooltip-title">
              Reaction time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-reaction-time-column-tooltip">
              Time from receiving a message to an assign, reply or archive action (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlq57`,
        },
      },
      avg_first_reaction_time: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-avg-first-reaction-time-column-label',
          )`First reaction time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-avg-first-reaction-time-column-tooltip-title">
              First reaction time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-avg-first-reaction-time-column-tooltip">
              Time from receiving the first message in a conversation to the first assign, reply or archive
              action (average)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/83hlq12`,
        },
      },
      num_messages_sent: {
        label: i18n._(
          t('analytics-metric-team-performance-table-num-messages-sent-column-label')`Messages sent`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-messages-sent-column-tooltip-title">
              Messages sent
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-messages-sent-column-tooltip">
              Outbound messages sent by your teammates
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqgg`,
        },
      },
      num_replies_sent: {
        label: i18n._(
          t('analytics-metric-team-performance-table-num-replies-sent-column-label')`Replies sent`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-replies-sent-column-tooltip-title">
              Replies sent
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-replies-sent-column-tooltip">
              Outbound messages sent by your teammates in reply to an inbound message
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/35hlqr8/replies-sent`,
        },
      },
      num_non_replies_sent: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-num-messages-sent-excluding-reply-column-label',
          )`Messages sent (excluding replies)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-messages-sent-excluding-reply-column-tooltip-title">
              Messages sent (excluding replies)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-messages-sent-excluding-reply-column-tooltip">
              Outbound messages sent by your teammates that were not replies to an inbound message
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/g9hlqgn`,
        },
      },
      num_archived_segments: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-num-archived-segments-column-label',
          )`Archived conversations`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-archived-segments-column-tooltip-title">
              Archived conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-archived-segments-column-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf`,
        },
      },
      num_assigned_segments: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-num-assigned-segments-column-label',
          )`Assigned conversations`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-assigned-segments-column-tooltip-title">
              Assigned conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-assigned-segments-column-tooltip">
              Conversations with activity this period while this teammate was the assignee
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/y4hz9qz`,
        },
      },
      num_touched_segments: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-num-touched-segments-column-label',
          )`Touched conversations`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-touched-segments-column-tooltip-title">
              Touched conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-touched-segments-column-tooltip">
              Conversations with an action taken by this teammate (applied a tag, sent a message, commented,
              etc)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlq28`,
        },
      },
      num_archived_segments_with_reply: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-num-archived-segments-with-reply-column-label',
          )`Archived with reply`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-archived-segments-with-reply-column-tooltip-title">
              Archived with reply
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-archived-segments-with-reply-column-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
              and had at least one reply
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hz9q7`,
        },
      },
      num_sla_breaches: {
        label: i18n._(
          t('analytics-metric-team-performance-table-num-sla-breaches-column-label')`SLA breaches`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-sla-breaches-column-tooltip-title">
              SLA breaches
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-sla-breaches-column-tooltip">
              Number of times an SLA breach tag was applied this period while this teammate was the assignee
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hlh58`,
        },
      },
      num_msg_templates_sent: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-num-msg-templates-sent-column-label',
          )`Message templates sent`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-num-msg-templates-sent-column-tooltip-title">
              Message templates sent
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-num-msg-templates-sent-column-tooltip">
              Number of messages sent that used a message template
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hgmhd/message-templates-sent`,
        },
      },
      csat_survey_satisfaction: {
        label: i18n._(
          t(
            'analytics-metric-team-performance-table-csat-survey-satisfaction-column-label',
          )`Satisfaction score (%)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-team-performance-table-csat-survey-satisfaction-column-tooltip-title">
              Satisfaction score (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-team-performance-table-csat-survey-satisfaction-column-tooltip">
              Percent of survey responses this period with 4 or 5 stars
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/p8hfdgd/satisfaction-score`,
        },
      },
    },
  },
  [AnalyticsTableMetricsEnum.SLA_STATUS_TABLE_V2]: {
    id: AnalyticsTableMetricsEnum.SLA_STATUS_TABLE_V2,
    label: i18n._(t('analytics-metric-sla-status-table-v2-label')`SLA Details`),
    tooltip: {
      title: <Trans id="analytics-metric-sla-status-table-v2-tooltip-title">SLA details</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-sla-status-table-v2-tooltip">
          Performance metrics for all of your existing SLA Rules for the selected time period.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/q6hh1bw/sla-rules`,
    },
    columns: {
      sla_rule: {
        label: i18n._(t('analytics-metric-sla-status-table-v2-sla-rule-column-label')`SLA rule`),
      },
      sla_time_goal: {
        label: i18n._(t('analytics-metric-sla-status-table-sla-v2-time-goal-column-label')`SLA goal`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-time-goal-column-tooltip-title">
              SLA goal
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-time-goal-column-tooltip">
              SLA time limit defined in this rule
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hz4m7/slas-reports`,
        },
      },
      avg_sla_breach_time: {
        label: i18n._(
          t(
            'analytics-metric-sla-status-table-sla-v2-avg-sla-breach-time-column-label',
          )`SLA breach time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-avg-sla-breach-time-column-tooltip-title">
              SLA breach time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-avg-sla-breach-time-column-tooltip">
              Time from the SLA breach to the next reply (average).
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/p8hlqrj/sla-breach-time-avg`,
        },
      },
      pct_sla_achieved: {
        label: i18n._(
          t('analytics-metric-sla-status-table-sla-v2-achieved-column-label')`SLA achievement (%)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-achieved-column-tooltip-title">
              SLA achievement (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-achieved-column-tooltip">
              Percentage of messages with SLA conditions that did not end up in breach
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hk472`,
        },
      },
      sla_warnings: {
        label: i18n._(t('analytics-metric-sla-status-table-sla-v2-warning-column-label')`SLA warnings`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-warning-column-tooltip-title">
              SLA warnings
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-warning-column-tooltip">
              Number of times an SLA warning tag was applied by this rule
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqr2/sla-warnings`,
        },
      },
      sla_applies: {
        label: i18n._(t('analytics-metric-sla-status-table-sla-v2-applies-column-label')`Messages with SLA`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-applies-column-tooltip-title">
              Messages with SLA
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-applies-column-tooltip">
              Number of messages that met SLA conditions this period.
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/35hk47r`,
        },
      },
      sla_breaches: {
        label: i18n._(t('analytics-metric-sla-status-table-sla-v2-breaches-column-label')`SLA breaches`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-breaches-column-tooltip-title">
              SLA breaches
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-sla-v2-breaches-column-tooltip">
              Number of times an SLA breach tag was applied by this rule
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
        },
      },
      num_current_breach: {
        label: i18n._(
          t(
            'analytics-metric-sla-status-table-v2-num-current-breach-column-label',
          )`Conversations in breach at end`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-sla-status-table-v2-num-current-breach-column-tooltip-title">
              Conversations in breach at end
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-sla-status-table-v2-num-current-breach-column-tooltip">
              Conversations still in SLA breach at the end of the period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlhwg/conversations-in-breach-at-end`,
        },
      },
    },
  },
  [AnalyticsTableMetricsEnum.ACCOUNTS_TABLE]: {
    id: AnalyticsTableMetricsEnum.ACCOUNTS_TABLE,
    label: i18n._(t('analytics-metric-accounts-table-label')`Accounts`),
    columns: {
      num_active_segments: {
        label: i18n._(
          t('analytics-metric-accounts-table-num-active-segments-column-label')`Active conversations`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-num-active-segments-column-tooltip-title">
              Active conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-num-active-segments-column-tooltip">
              Conversations with at least one activity (message, tag added, etc) this period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/g9hlqkf/active-conversations`,
        },
      },
      num_new_segments: {
        label: i18n._(t('analytics-metric-accounts-table-num-new-segments-column-label')`New conversations`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-num-new-segments-column-tooltip-title">
              New conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-num-new-segments-column-tooltip">
              New conversations started by customers or your teammates
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqjy/new-conversations`,
        },
      },
      num_archived_segments: {
        label: i18n._(
          t('analytics-metric-accounts-table-num-archived-segments-column-label')`Archived conversations`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-num-archived-segments-column-tooltip-title">
              Archived conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-num-archived-segments-column-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
        },
      },
      num_received: {
        label: i18n._(t('analytics-metric-accounts-table-num-received-column-label')`Messages received from`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-num-received-column-tooltip-title">
              Messages received from
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-num-received-column-tooltip">
              Messages received from customer
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlqpv`,
        },
      },
      num_sent: {
        label: i18n._(t('analytics-metric-accounts-table-num-sent-column-label')`Messages sent to`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-num-sent-column-tooltip-title">Messages sent to</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-num-sent-column-tooltip">
              Messages sent to customer
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqgg`,
        },
      },
      avg_reply_time: {
        label: i18n._(t('analytics-metric-accounts-table-avg-response-column-label')`Reply time (avg)`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-avg-response-column-tooltip-title">
              Reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-avg-response-column-tooltip">
              Average response time for customer
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlqr6`,
        },
      },
      avg_total_reply_time: {
        label: i18n._(
          t('analytics-metric-accounts-table-avg-resolution-column-label')`Total reply time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-avg-resolution-column-tooltip-title">
              Total reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-avg-resolution-column-tooltip">
              Cumulative time elapsed between receiving and replying to messages (average per conversation)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc`,
        },
      },
      avg_first_reply_time: {
        label: i18n._(
          t('analytics-metric-accounts-table-avg-first-response-column-label')`First reply time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-avg-first-response-column-tooltip-title">
              First reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-avg-first-response-column-tooltip">
              Average first reply time for customer
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlq1a`,
        },
      },
      sla_breaches: {
        label: i18n._(t('analytics-metric-accounts-table-sla-breaches-column-label')`SLA breaches`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-sla-breaches-column-tooltip-title">SLA breaches</Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-sla-breaches-column-tooltip">
              Number of times an SLA breach tag was applied
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
        },
      },
      avg_csat_survey_score: {
        label: i18n._(
          t('analytics-metric-accounts-table-csat-survey-satisfaction-column-label')`Satisfaction score (%)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-accounts-table-csat-survey-satisfaction-column-tooltip-title">
              Satisfaction score (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-accounts-table-csat-survey-satisfaction-column-tooltip">
              Percent of survey responses this period with 4 or 5 stars
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/p8hfdgd/satisfaction-score`,
        },
      },
    },
  },
  [AnalyticsTableMetricsEnum.CUSTOMERS_TABLE_V2]: {
    id: AnalyticsTableMetricsEnum.CUSTOMERS_TABLE_V2,
    label: i18n._(t('analytics-metric-customers-table-v2-label')`Contacts`),
    columns: {
      num_active_segments: {
        label: i18n._(
          t('analytics-metric-customers-table-v2-num-active-segments-column-label')`Active conversations`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-num-active-segments-column-tooltip-title">
              Active conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-num-active-segments-column-tooltip">
              Conversations with at least one activity (message, tag added, etc) this period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/g9hlqkf/active-conversations`,
        },
      },
      num_new_segments: {
        label: i18n._(
          t('analytics-metric-customers-table-v2-num-new-segments-column-label')`New conversations`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-num-new-segments-column-tooltip-title">
              New conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-num-new-segments-column-tooltip">
              New conversations started by customers or your teammates
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqjy/new-conversations`,
        },
      },
      num_archived_segments: {
        label: i18n._(
          t('analytics-metric-customers-table-v2-num-archived-segments-column-label')`Archived conversations`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-num-archived-segments-column-tooltip-title">
              Archived conversations
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-num-archived-segments-column-tooltip">
              Conversations archived or snoozed this period that remained archived at the end of the period
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hz9qf/archived-conversations`,
        },
      },
      num_received: {
        label: i18n._(
          t('analytics-metric-customers-table-v2-num-received-column-label')`Messages received from`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-num-received-column-tooltip-title">
              Messages received from
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-num-received-column-tooltip">
              Messages received from customer
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlqpv`,
        },
      },
      num_sent: {
        label: i18n._(t('analytics-metric-customers-table-v2-num-sent-column-label')`Messages sent to`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-num-sent-column-tooltip-title">
              Messages sent to
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-num-sent-column-tooltip">
              Messages sent to customer
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/60hlqgg`,
        },
      },
      avg_reply_time: {
        label: i18n._(t('analytics-metric-customers-table-v2-avg-response-column-label')`Reply time (avg)`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-avg-response-column-tooltip-title">
              Reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-avg-response-column-tooltip">
              Average response time for customer
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/q6hlqr6`,
        },
      },
      avg_total_reply_time: {
        label: i18n._(
          t('analytics-metric-customers-table-v2-avg-resolution-column-label')`Total reply time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-avg-resolution-column-tooltip-title">
              Total reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-avg-resolution-column-tooltip">
              Cumulative time elapsed between receiving and replying to messages (average per conversation)
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/m1hlqrc`,
        },
      },
      avg_first_reply_time: {
        label: i18n._(
          t('analytics-metric-customers-table-v2-avg-first-response-column-label')`First reply time (avg)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-avg-first-response-column-tooltip-title">
              First reply time (avg)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-avg-first-response-column-tooltip">
              Average first reply time for customer
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/x2hlq1a`,
        },
      },
      sla_breaches: {
        label: i18n._(t('analytics-metric-customers-table-v2-sla-breaches-column-label')`SLA breaches`),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-sla-breaches-column-tooltip-title">
              SLA breaches
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-sla-breaches-column-tooltip">
              Number of times an SLA breach tag was applied
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/h7hlh58/sla-breaches`,
        },
      },
      avg_csat_survey_score: {
        label: i18n._(
          t(
            'analytics-metric-customers-table-v2-csat-survey-satisfaction-column-label',
          )`Satisfaction score (%)`,
        ),
        tooltip: {
          title: (
            <Trans id="analytics-metric-customers-table-v2-csat-survey-satisfaction-column-tooltip-title">
              Satisfaction score (%)
            </Trans>
          ),
          tooltipContent: (
            <Trans id="analytics-metric-customers-table-v2-csat-survey-satisfaction-column-tooltip">
              Percent of survey responses this period with 4 or 5 stars
            </Trans>
          ),
          learnMoreUrl: `${helpCenterUrl}/t/p8hfdgd/satisfaction-score`,
        },
      },
    },
  },
  [AnalyticsTableMetricsEnum.CSAT_SURVEY_SAMPLE_FEEDBACK_TABLE]: {
    id: AnalyticsTableMetricsEnum.CSAT_SURVEY_SAMPLE_FEEDBACK_TABLE,
    label: i18n._(t('analytics-metric-sample-feedback-table-label')`Sample Feedback`),
  },

  // Sankey
  [AnalyticsComputedGraphMetricsEnum.SANKEY_GRAPH]: {
    id: AnalyticsComputedGraphMetricsEnum.SANKEY_GRAPH,
    label: i18n._(t('analytics-metric-conversation-flows-label')`Conversation Flows`),
    tooltip: {
      title: <Trans id="analytics-metric-conversation-flows-tooltip-title">Conversation flows</Trans>,
      tooltipContent: (
        <Trans id="analytics-metric-conversation-flows-tooltip">
          Status changes of conversations during your selected time range.
        </Trans>
      ),
      learnMoreUrl: `${helpCenterUrl}/t/p8hz4mb/conversations-report#conversation-flows-chart`,
    },
  },
};

export const getMetricLabels = <T extends AnyMetricKey>(metric?: T): Partial<MetricWithLabel<T>> => {
  if (!metric) {
    return {};
  }

  // For some reason TS doesn't consider `metricsLabels[metric]` as a valid return value matching `Partial<MetricWithLabel<T>>`.
  return (metricsLabels[metric] as MetricWithLabel<T>) || {id: metric, label: undefined};
};
